import { getSearchList } from "@/api/endpoints/searches";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
} from "@components/ui/sidebar";
import { useQuery } from "@tanstack/react-query";
import { ChevronRightIcon } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useMemo } from "react";
import { Spinner } from "@components/ui/spinner";
import Error from "@components/ui/error";
import { ActiveSearches } from "./ActiveSearches";
import { SavedSearches } from "./SavedSearches";
import { InactiveSearches } from "./InactiveSearches";
import { EmptySearches } from "./EmptySearches";
import AccessControl from "@components/AccessControl";
import { Feature } from "@components/Feature";
import { FEATURE } from "@/features";
import { groupSearches } from "@/lib/alert";

export function SearchesNav({
  orgUuid,
  pathname,
}: {
  orgUuid: string;
  pathname: string;
}) {
  const { t } = useLingui();
  const [isSearchesOpen, setIsSearchesOpen] = useLocalStorage(
    "sidebar-searches-open",
    true
  );

  const {
    data: searches,
    isLoading: isSearchesLoading,
    error: searchesError,
  } = useQuery({
    queryKey: [orgUuid, "searches"],
    queryFn: () => getSearchList({ light: true }),
    refetchOnWindowFocus: false,
  });

  const { activeSearches, savedSearches, inactiveSearches, hasSearches } =
    useMemo(() => {
      if (searches?.ok) {
        const { active, inactive, saved } = groupSearches(searches.data);

        return {
          activeSearches: active,
          savedSearches: saved,
          inactiveSearches: inactive,
          hasSearches:
            active.length > 0 || saved.length > 0 || inactive.length > 0,
        };
      }

      return {
        activeSearches: [],
        savedSearches: [],
        inactiveSearches: [],
        hasSearches: false,
      };
    }, [searches]);

  return (
    <Collapsible
      open={isSearchesOpen}
      onOpenChange={setIsSearchesOpen}
      className="group/searches"
      data-testid="mention-side-nav"
    >
      <SidebarGroup>
        <SidebarGroupLabel asChild>
          <CollapsibleTrigger>
            <Trans>Bevakningar</Trans>
            <ChevronRightIcon className="ml-auto transition-transform group-data-[state=open]/searches:rotate-90" />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          {searches?.ok ? (
            hasSearches ? (
              <SidebarGroupContent>
                <SidebarMenu>
                  <ActiveSearches
                    activeSearches={activeSearches}
                    orgUuid={orgUuid}
                    pathname={pathname}
                  />

                  <Feature required={FEATURE.HAS_SEARCH_PRODUCT} upsale>
                    <AccessControl>
                      {savedSearches.length > 0 ? (
                        <SavedSearches
                          savedSearches={savedSearches}
                          orgUuid={orgUuid}
                          pathname={pathname}
                        />
                      ) : null}
                    </AccessControl>
                  </Feature>

                  {inactiveSearches.length > 0 ? (
                    <InactiveSearches
                      inactiveSearches={inactiveSearches}
                      orgUuid={orgUuid}
                      pathname={pathname}
                    />
                  ) : null}
                </SidebarMenu>
              </SidebarGroupContent>
            ) : (
              <EmptySearches orgUuid={orgUuid} />
            )
          ) : isSearchesLoading ? (
            <Spinner className="mx-auto" />
          ) : (
            <Error
              className="py-4 md:py-6"
              error={searchesError}
              heading={t`Åh nej!`}
              description={t`Något gick snett med att ladda bevakningarna. Vänligen försök igen senare`}
            />
          )}
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}
